// Drop ABSOLUTE font sizes from pasted content.
//
// Why this lives in the element and NOT in the shared extension list:
// `FontSize` is shared with @o2d/whiteboard, and the whiteboard's textbox sets,
// reads and exports absolute PIXEL sizes on exactly this mark — its SVG export
// accepts `px` and nothing else (`textbox-to-svg-text.ts`), and its own paste
// path deliberately keeps them for text copied within the board. Removing
// absolute sizes inside the shared `FontSize.parseHTML` would therefore damage
// the textbox and its export, not just normalise a Word paste. Measured before
// the decision (2026-09-18); do not "simplify" this back into the extension.
//
// What it does: a paste from Word, Google Docs or a browser carries e.g.
// `font-size: 18pt`, which renders in a size unrelated to the surrounding
// application text. Such a value is DISCARDED — the run then reads at the size
// that applies where it landed. It is deliberately not mapped onto the
// toolbar's four steps: 18pt sits between two of them, and mapping would
// reinterpret someone else's intent.
//
// What it leaves alone: everything else on the mark and around it — bold,
// italic, underline, colour, highlight, links and block structure all survive a
// paste untouched. The editor does NOT adopt the insertion point's formatting
// (that is the whiteboard textbox's own, deliberate rule; it was not asked for
// here). Relative sizes (`em`, `rem`, `%`, `ex`, `ch`) also survive: the
// toolbar's own steps are `em`, so a run copied within the editor keeps its
// size.

import { Fragment, Slice } from "@tiptap/pm/model";
import type { Mark, Node as ProseMirrorNode } from "@tiptap/pm/model";

/** CSS length units that scale with the surrounding text. */
const RELATIVE_UNIT = /(?:em|rem|%|ex|ch)$/i;

/**
 * Whether a `textStyle.fontSize` value is absolute, i.e. independent of the
 * surrounding text size (`pt`, `px`, `cm`, `in`, `mm`, `pc`, or a bare number).
 * An empty or non-string value is not a size and counts as not absolute.
 */
export function isAbsoluteFontSize(value: unknown): boolean {
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (trimmed === "") {
    return false;
  }
  return !RELATIVE_UNIT.test(trimmed);
}

/** The node's marks with any absolute `fontSize` cleared, or the marks as they were. */
function withoutAbsoluteFontSize(marks: readonly Mark[]): Mark[] {
  return marks.map((mark) => {
    if (!isAbsoluteFontSize(mark.attrs?.["fontSize"])) {
      return mark;
    }
    // Clear only this attribute — `color` and every other attribute on the same
    // mark stay, so a pasted red 18pt run keeps its red.
    return mark.type.create({ ...mark.attrs, fontSize: null });
  });
}

/** Recursively rebuild a fragment with absolute font sizes cleared on text runs. */
function stripFragment(fragment: Fragment): Fragment {
  const nodes: ProseMirrorNode[] = [];
  fragment.forEach((node) => {
    if (node.isText) {
      nodes.push(node.mark(withoutAbsoluteFontSize(node.marks)));
      return;
    }
    nodes.push(node.copy(stripFragment(node.content)));
  });
  return Fragment.fromArray(nodes);
}

/**
 * ProseMirror `transformPasted`: hand back the pasted slice with every absolute
 * font size removed. Block structure and open depths are preserved, so a pasted
 * list stays a list.
 */
export function stripAbsoluteFontSizes(slice: Slice): Slice {
  return new Slice(stripFragment(slice.content), slice.openStart, slice.openEnd);
}
